import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function PricingToggleSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const plans = content.plans || [];
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'cards';
    const columns = config.columns || 3;
    const [isAnnual, setIsAnnual] = useState(false);

    const toggleLabels = content.toggle_labels || ['Mensual', 'Anual'];
    const saveText = content.save_text || 'Ahorra 20%';

    if (plans.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const cols = columns === 2 ? 'md:grid-cols-2 max-w-3xl' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4 max-w-6xl' : 'md:grid-cols-3 max-w-5xl';

    const Toggle = () => (
        <div className={`flex items-center justify-center gap-3 mb-10 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="inline-flex items-center rounded-full p-1" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                <button onClick={() => setIsAnnual(false)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${!isAnnual ? 'text-white shadow-md' : ''}`}
                    style={!isAnnual ? { backgroundColor: s.accent } : { color: s.textSub }}>
                    {toggleLabels[0]}
                </button>
                <button onClick={() => setIsAnnual(true)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${isAnnual ? 'text-white shadow-md' : ''}`}
                    style={isAnnual ? { backgroundColor: s.accent } : { color: s.textSub }}>
                    {toggleLabels[1]}
                </button>
            </div>
            {isAnnual && saveText && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold text-white animate-pulse" style={{ backgroundColor: s.accent }}>
                    {saveText}
                </span>
            )}
        </div>
    );

    const Checkmark = () => (
        <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
    );

    // ── Comparison variant (table-style) ──
    if (variant === 'comparison') {
        // Collect all unique features
        const allFeatures = [];
        plans.forEach(plan => {
            const feats = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
            feats.forEach(f => { const txt = (typeof f === 'string' ? f : f.text || '').trim(); if (txt && !allFeatures.includes(txt)) allFeatures.push(txt); });
        });

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className="overflow-x-auto">
                        <table className="w-full max-w-4xl mx-auto">
                            <thead>
                                <tr>
                                    <th className="text-left py-4 px-4" style={{ color: s.textSub }}></th>
                                    {plans.map((plan, i) => (
                                        <th key={i} className="text-center py-4 px-4 min-w-[160px]">
                                            {plan.badge && plan.highlighted && (
                                                <span className="inline-block px-3 py-0.5 rounded-full text-[10px] font-bold text-white mb-2" style={{ backgroundColor: s.accent }}>{plan.badge}</span>
                                            )}
                                            <div className="font-bold text-base" style={{ color: s.text }}>{plan.name}</div>
                                            <div className="mt-2 transition-all duration-300">
                                                <span className="text-3xl font-extrabold" style={{ color: plan.highlighted ? s.accent : s.text }}>
                                                    {plan.currency || '$'}{isAnnual ? plan.annual_price : plan.monthly_price}
                                                </span>
                                                <span className="text-sm ml-1" style={{ color: s.textSub }}>{isAnnual ? '/anual' : '/mes'}</span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {allFeatures.map((feat, fi) => (
                                    <tr key={fi} className="border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6' }}>
                                        <td className="py-3 px-4 text-sm" style={{ color: s.text }}>{feat}</td>
                                        {plans.map((plan, pi) => {
                                            const feats = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
                                            const has = feats.some(f => (typeof f === 'string' ? f : f.text || '').trim() === feat);
                                            return (
                                                <td key={pi} className="text-center py-3 px-4">
                                                    {has ? (
                                                        <svg className="w-5 h-5 mx-auto" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-5 h-5 mx-auto opacity-30" style={{ color: s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                                {/* CTA row */}
                                <tr className="border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6' }}>
                                    <td className="py-6 px-4"></td>
                                    {plans.map((plan, i) => (
                                        <td key={i} className="text-center py-6 px-4">
                                            {plan.cta_text && (
                                                <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                                    className={`inline-block px-6 py-2.5 rounded-xl text-sm font-semibold transition-all hover:shadow-lg ${plan.highlighted ? 'text-white' : 'border-2'}`}
                                                    style={plan.highlighted ? { backgroundColor: s.accent } : { borderColor: s.accent, color: s.accent }}>
                                                    {plan.cta_text}
                                                </a>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Minimal variant ──
    if (variant === 'minimal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <Toggle />
                    <div className="max-w-4xl mx-auto divide-y" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6' }}>
                        {plans.map((plan, i) => {
                            const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
                            return (
                                <div key={i} className={`flex flex-col md:flex-row items-start md:items-center justify-between gap-4 py-6 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                                    style={{ transitionDelay: visible ? `${200 + i * 100}ms` : '0ms' }}>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-lg font-bold" style={{ color: s.text }}>{plan.name}</h3>
                                            {plan.highlighted && plan.badge && (
                                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: s.accent }}>{plan.badge}</span>
                                            )}
                                        </div>
                                        {plan.description && <p className="text-sm mt-1" style={{ color: s.textSub }}>{plan.description}</p>}
                                        {features.length > 0 && (
                                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                                                {features.slice(0, 4).map((f, fi) => (
                                                    <span key={fi} className="flex items-center gap-1.5 text-xs" style={{ color: s.textSub }}>
                                                        <Checkmark />{typeof f === 'string' ? f.trim() : f.text || f}
                                                    </span>
                                                ))}
                                                {features.length > 4 && <span className="text-xs" style={{ color: s.textSub }}>+{features.length - 4} mas</span>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4 shrink-0">
                                        <div className="text-right">
                                            <span className="text-2xl font-extrabold transition-all duration-300" style={{ color: plan.highlighted ? s.accent : s.text }}>
                                                {plan.currency || '$'}{isAnnual ? plan.annual_price : plan.monthly_price}
                                            </span>
                                            <span className="text-xs ml-1" style={{ color: s.textSub }}>{isAnnual ? '/anual' : '/mes'}</span>
                                        </div>
                                        {plan.cta_text && (
                                            <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:shadow-lg whitespace-nowrap ${plan.highlighted ? 'text-white' : 'border-2'}`}
                                                style={plan.highlighted ? { backgroundColor: s.accent } : { borderColor: s.accent, color: s.accent }}>
                                                {plan.cta_text}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <Toggle />
                <div className={`grid grid-cols-1 ${cols} mx-auto gap-6`}>
                    {plans.map((plan, i) => {
                        const features = Array.isArray(plan.features) ? plan.features : (plan.features || '').split('\n').filter(f => f.trim());
                        const displayPrice = isAnnual ? plan.annual_price : plan.monthly_price;
                        return (
                            <div key={i} className={`relative rounded-2xl p-1 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${
                                plan.highlighted ? 'shadow-xl scale-[1.02]' : 'shadow-sm'
                            }`}
                                style={{
                                    background: plan.highlighted ? `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.8)})` : undefined,
                                    transitionDelay: visible ? `${200 + i * 100}ms` : '0ms',
                                }}>
                                {plan.highlighted && plan.badge && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white z-10" style={{ backgroundColor: s.accent }}>
                                        {plan.badge}
                                    </div>
                                )}
                                <div className={`rounded-xl p-6 h-full flex flex-col ${plan.highlighted ? '' : 'border'}`}
                                    style={{
                                        backgroundColor: s.dark ? (plan.highlighted ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)') : '#ffffff',
                                        borderColor: plan.highlighted ? 'transparent' : (s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6'),
                                    }}>
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{plan.name}</h3>
                                    {plan.description && <p className="text-sm mt-1" style={{ color: s.textSub }}>{plan.description}</p>}
                                    <div className="mt-4 mb-6">
                                        <div className="relative h-12 overflow-hidden">
                                            <div className="transition-all duration-500 ease-out" style={{ transform: isAnnual ? 'translateY(-100%)' : 'translateY(0)' }}>
                                                <div className="h-12 flex items-end">
                                                    <span className="text-4xl font-extrabold" style={{ color: plan.highlighted ? s.accent : s.text }}>
                                                        {plan.currency || '$'}{plan.monthly_price}
                                                    </span>
                                                    <span className="text-sm ml-1 mb-1" style={{ color: s.textSub }}>/mes</span>
                                                </div>
                                                <div className="h-12 flex items-end">
                                                    <span className="text-4xl font-extrabold" style={{ color: plan.highlighted ? s.accent : s.text }}>
                                                        {plan.currency || '$'}{plan.annual_price}
                                                    </span>
                                                    <span className="text-sm ml-1 mb-1" style={{ color: s.textSub }}>/anual</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {features.length > 0 && (
                                        <ul className="space-y-3 flex-1 mb-6">
                                            {features.map((f, fi) => (
                                                <li key={fi} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                                                    <Checkmark />
                                                    {typeof f === 'string' ? f.trim() : f.text || f}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {plan.cta_text && (
                                        <a {...resolveLinkProps(plan.cta_link || '#contact')}
                                            className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all hover:shadow-lg ${
                                                plan.highlighted ? 'text-white' : 'border-2'
                                            }`}
                                            style={plan.highlighted
                                                ? { backgroundColor: s.accent }
                                                : { borderColor: s.accent, color: s.accent }}>
                                            {plan.cta_text}
                                        </a>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
